import React, { useMemo, useState } from 'react';
import { HandIcon, SearchIcon } from 'lucide-react';
import { InitialAvatar } from '../../components/InitialAvatar';
import { ChoiceChip } from '../../components/ui/ChoiceChip';
import { FilterMenu } from '../../components/ui/FilterMenu';
import { TextInput } from '../../components/ui/TextControl';
import { YearsChart } from '../../components/people/YearsChart';
import { PersonDetail } from '../../components/people/PersonDetail';
import { BadgeMark } from '../../components/profile/MemberBadges';
import { PrototypeNote } from '../../components/prototype/PrototypeNote';
import { prototypeStateFor } from '../../data/prototypeStates';
import { AFFILIATION_LABELS, CURRENT_MEMBER, DECADES, overlapWith, publicName, scaleMembers } from '../../data/community';
import type { Affiliation, Member } from '../../data/community';
interface PeopleProps {
  sparse: boolean;
  onOpenProfile: (member: Member) => void;
}
const AFFILIATIONS: Affiliation[] = ['alumni', 'staff', 'volunteer', 'board', 'supporter'];
export function People({
  sparse,
  onOpenProfile
}: PeopleProps) {
  const [query, setQuery] = useState('');
  const [decade, setDecade] = useState<string | null>(null);
  const [affiliation, setAffiliation] = useState<Affiliation | null>(null);
  const [overlapOnly, setOverlapOnly] = useState(false);
  const [view, setView] = useState<'years' | 'list'>('years');
  const [selected, setSelected] = useState<Member | null>(null);
  const all = scaleMembers(sparse);
  const results = useMemo(() => all.filter(member => {
    if (decade && member.decade !== decade) return false;
    if (affiliation && member.affiliation !== affiliation) return false;
    if (overlapOnly && !overlapWith(member, CURRENT_MEMBER)) return false;
    if (query && !publicName(member.name).toLowerCase().includes(query.toLowerCase())) {
      return false;
    }
    return true;
  }), [all, decade, affiliation, overlapOnly, query]);
  const overlapping = results.filter(member => member.name !== CURRENT_MEMBER.fullName && overlapWith(member, CURRENT_MEMBER)).length;
  return <div className="min-h-full bg-card" data-id="element-740" data-mp="8m4u3jbngz7i">
      <div className="border-b border-rule bg-canvas" data-id="element-741" data-mp="3kffjtp7y3eo">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-4 py-6 sm:px-6" data-id="element-742" data-mp="3n1nhug0ukxf">
          <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1" data-id="element-743" data-mp="3vqkodvfymudo">
            <h1 className="text-section-heading font-semibold text-ink" data-id="element-744" data-mp="w94lq754syxa">People</h1>
            <p className="text-small text-ink-muted" data-id="element-745" data-mp="rbkwydat53r3">
              {results.length} of {all.length} shown
              {overlapping > 0 ? ` · ${overlapping} were here when you were` : ''}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3" data-id="element-746" data-mp="ihzh0qmwpgg3">
            <label className="relative flex min-w-56 flex-1 items-center" data-id="element-747" data-mp="1f0du2d361hna">
              <SearchIcon aria-hidden="true" className="pointer-events-none absolute left-3.5 h-4 w-4 text-ink-muted" data-id="element-748" data-mp="1a9nqn5eavov8" />
              <span className="sr-only" data-id="element-749" data-mp="2sy05kobaneav">Search by first name or last initial</span>
              <TextInput type="search" placeholder="Search by first name or last initial" className="pl-10" value={query} onChange={event => setQuery(event.target.value)} data-id="element-750" data-mp="2xvs4w3e9q2p3" />
            </label>

            <div className="inline-flex rounded-pill border border-rule p-1" role="group" aria-label="View" data-id="element-751" data-mp="3bu8afr0e9n5n">
              {(['years', 'list'] as const).map(item => <button key={item} type="button" aria-pressed={view === item} onClick={() => setView(item)} className={`inline-flex min-h-[2.25rem] items-center rounded-pill px-4 text-small transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${view === item ? 'bg-ink font-medium text-on-ink' : 'text-ink-muted hover:text-ink'}`} data-id="element-752" data-mp="3lmxkqik7kuze">
                  {item === 'years' ? 'Fifty years' : 'List'}
                </button>)}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2" data-id="element-753" data-mp="32z0zemzyx5xf">
            <ChoiceChip checked={overlapOnly} onChange={() => setOverlapOnly(!overlapOnly)} className="shrink-0" data-id="element-754" data-mp="32z9gys71czey">
              Here when I was
            </ChoiceChip>

            <span aria-hidden="true" className="h-6 w-px shrink-0 bg-rule" data-id="element-755" data-mp="3cuueoe52ndiw" />

            <div className="shrink-0" data-id="element-756" data-mp="37wstnno3544s">
              <FilterMenu label="Years" allLabel="All" value={decade} onChange={setDecade} options={[{
              label: 'All years',
              value: null
            }, ...DECADES.map(item => ({
              label: item,
              value: item
            }))]} data-id="element-757" data-mp="s6pvxv6bziq6" />
            </div>

            <div className="shrink-0" data-id="element-758" data-mp="zoe8tt8ypoeq">
              <FilterMenu label="Connection" allLabel="Any" value={affiliation} onChange={next => setAffiliation(next as Affiliation | null)} options={[{
              label: 'Any connection',
              value: null
            }, ...AFFILIATIONS.map(item => ({
              label: AFFILIATION_LABELS[item],
              value: item
            }))]} data-id="element-759" data-mp="lr7p9wgezlog" />
            </div>

            {decade || affiliation || overlapOnly || query ? <button type="button" onClick={() => {
            setDecade(null);
            setAffiliation(null);
            setOverlapOnly(false);
            setQuery('');
          }} className="inline-flex min-h-tap shrink-0 items-center rounded-pill px-3 text-small text-ink-muted underline-offset-4 hover:text-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2" data-id="element-760" data-mp="1wgrkmjty55fy">
                Clear
              </button> : null}
          </div>
        </div>
      </div>

      <div className="mx-auto grid w-full max-w-6xl gap-6 px-4 pb-16 pt-2 sm:px-6 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start" data-id="element-761" data-mp="1s4ujt90i9vjh">
        <div data-id="element-762" data-mp="1nckvtyiyb5ju">
          {results.length === 0 ? <div className="mt-10 rounded-card border border-dashed border-rule p-10 text-center" data-id="element-763" data-mp="1jbr38rukd2km">
              <p className="text-body text-ink" data-id="element-764" data-mp="1edz3jbnogc5v">Nobody matches that yet.</p>
              <p className="mx-auto mt-2 max-w-sm text-small text-ink-muted" data-id="element-765" data-mp="19e38i1wyjv2g">
                These years are thin. Whoever arrives next from them will be looking for exactly
                the account you could leave.
              </p>
            </div> : view === 'years' ? <YearsChart members={results} selected={selected} onSelect={setSelected} data-id="element-766" data-mp="udfrdt211qn1" /> : <ul className="flex flex-col divide-y divide-rule" data-id="element-767" data-mp="14998zqulsabo">
              {results.map(member => {
            const overlap = overlapWith(member, CURRENT_MEMBER);
            const isYou = member.name === CURRENT_MEMBER.fullName;
            return <li key={member.name} data-id="element-768" data-mp="bmeqv0l9dn1d">
                    <button type="button" onClick={() => setSelected(member)} className="flex w-full items-center gap-4 py-4 text-left hover:bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1" data-id="element-769" data-mp="gjevd72gmhqd">
                      <InitialAvatar name={member.name} size={44} data-id="element-770" data-mp="3oyylvybqxm1q" />
                      <span className="flex min-w-0 flex-1 flex-col" data-id="element-771" data-mp="3txrr73gvq6qt">
                        <span className="flex items-center gap-1.5 text-body font-medium text-ink" data-id="element-772" data-mp="1bznfhz9fwlqc">
                          {isYou ? 'You' : publicName(member.name)}
                          <BadgeMark name={member.name} data-id="element-773" data-mp="17zv0b3pjjhc0" />
                        </span>
                        <span className="truncate text-small text-ink-muted" data-id="element-774" data-mp="1lze8fwicqsob">
                          {member.years} · {AFFILIATION_LABELS[member.affiliation]}
                          {member.city ? ` · ${member.city}` : ''}
                        </span>
                        {prototypeStateFor(member.name) ? <PrototypeNote className="mt-1 w-fit" data-id="element-775" data-mp="1qutl4l4miij5">
                            {prototypeStateFor(member.name)!.label}
                          </PrototypeNote> : null}
                      </span>
                      {member.memorial ? <span className="shrink-0 text-small text-ink-muted" data-id="element-776" data-mp="2en0fjeqsgffx">In memory</span> : overlap && !isYou ? <span className="hidden shrink-0 rounded-pill bg-primary-soft px-3 py-1 text-small text-primary-text sm:inline" data-id="element-777" data-mp="2t9gwb828kt37">
                          {overlap.start}–{overlap.end} together
                        </span> : null}
                      {!member.memorial ? <HandIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-ink-muted" data-id="element-778" data-mp="2o9l4062wiq3h" /> : null}
                    </button>
                  </li>;
          })}
            </ul>}
        </div>

        <div className="lg:sticky lg:top-24" data-id="element-779" data-mp="20ll5ul39fxev">
          <PersonDetail member={selected} onClose={() => setSelected(null)} onOpenProfile={onOpenProfile} data-id="element-780" data-mp="20ip4ems7eqlo" />
        </div>
      </div>
    </div>;
}